import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getDashboard } from "../../services/api";
import globalStyles, { COLORS } from "../../styles/globalStyles";

import PeopleIcon from "@mui/icons-material/People";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import AssignmentIcon from "@mui/icons-material/Assignment";
import TvIcon from "@mui/icons-material/Tv";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import ContactPhoneIcon from "@mui/icons-material/ContactPhone";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import HandshakeIcon from "@mui/icons-material/Handshake";
import CancelIcon from "@mui/icons-material/Cancel";
import GroupsIcon from "@mui/icons-material/Groups";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";

const LEAD_STAGES = [
  "prospecting",
  "contacted",
  "demo_scheduled",
  "demo_done",
  "negotiation",
  "closed_won",
  "closed_lost",
];

const stageIcons = {
  prospecting: PendingActionsIcon,
  contacted: ContactPhoneIcon,
  demo_scheduled: EventAvailableIcon,
  demo_done: FactCheckIcon,
  negotiation: HandshakeIcon,
  closed_won: CheckCircleIcon,
  closed_lost: CancelIcon,
};

const stageColors = {
  prospecting: "#8b5cf6",
  contacted: "#0284c7",
  demo_scheduled: "#2563eb",
  demo_done: "#059669",
  negotiation: "#d97706",
  closed_won: "#10b981",
  closed_lost: "#ef4444",
};

const formatLabel = (value) => {
  if (!value) return "—";

  return String(value)
    .replace(/_/g, " ")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const s = {
  dynamicGrid: (count = 3, isMobile = false) => ({
    display: "grid",
    gridTemplateColumns: isMobile
      ? "1fr"
      : `repeat(${count % 4 === 0 ? 4 : 3}, minmax(0, 1fr))`,
    gap: 16,
    marginBottom: 28,
    width: "100%",
  }),

  tableHeaderRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 14,
    gap: 10,
  },

  tableTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: "#374151",
    textTransform: "uppercase",
    letterSpacing: "0.4px",
    margin: 0,
  },

  seeAllBtn: {
    background: "transparent",
    color: COLORS.primary,
    border: "none",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 700,
    padding: 0,
  },
};

const KPI = ({ value, label, icon: Icon, color, onClick }) => {
  const [hover, setHover] = useState(false);

  return (
    <div
      style={{
        ...globalStyles.tile,
        ...(hover ? globalStyles.activeTile : {}),
      }}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <div style={globalStyles.tileIcon(color)}>
        <Icon fontSize="medium" />
      </div>

      <div style={globalStyles.tileText}>
        <div style={globalStyles.tileValue}>{value ?? 0}</div>
        <div style={globalStyles.tileLabel}>{label}</div>
      </div>
    </div>
  );
};

const getStageCountFromResponse = (leadsPerStage = [], stage) => {
  const found = leadsPerStage.find((item) => item._id === stage);
  return found ? (found.count ?? 0) : 0;
};

const getRecentLeadValue = (lead, key) => {
  if (!lead) return "—";

  const map = {
    operator: lead.operator || lead.operatorId?.name || "—",
    stage: lead.stage || lead.currentStage || "—",
    status: lead.status || "—",
    assignedTo: lead.assignedTo?.name || lead.assignedTo || "—",
  };

  return map[key] || "—";
};

const useIsMobile = (breakpoint = 768) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= breakpoint);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= breakpoint);

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [breakpoint]);

  return isMobile;
};

export default function Dashboard() {
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const isMobile = useIsMobile();

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const dashboardRes = await getDashboard();
        const dashboardData = dashboardRes?.data?.data || {};
        setData(dashboardData);
      } catch (error) {
        console.error("Dashboard error:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, []);

  const { kpis = {}, leadsPerStage = [], recent_leads = [] } = data || {};

  const overviewTiles = useMemo(() => {
    return [
      {
        value: kpis.totalUsers ?? 0,
        label: "Total Users",
        icon: PeopleIcon,
        color: "#22c55e",
        path: "/users",
      },
      {
        value: kpis.totalOperators ?? 0,
        label: "Total Operators",
        icon: SupportAgentIcon,
        color: "#06b6d4",
        path: "/operators",
      },
      {
        value: kpis.conversionRate ?? "0%",
        label: "Conversion Rate",
        icon: TrendingUpIcon,
        color: "#f59e0b",
        path: "/leads",
      },
      {
        value: kpis.teamSize ?? 0,
        label: "Team Size",
        icon: GroupsIcon,
        color: "#8b5cf6",
        path: "/users",
      },
    ];
  }, [kpis]);

  const demoTiles = useMemo(() => {
    return [
      {
        value: kpis.totalDemos ?? 0,
        label: "Total Demos",
        icon: TvIcon,
        color: "#06b6d4",
        path: "/demos",
      },
      {
        value: kpis.scheduledDemos ?? 0,
        label: "Scheduled Demos",
        icon: EventAvailableIcon,
        color: "#2563eb",
        path: "/demos",
      },
      {
        value: kpis.completedDemos ?? 0,
        label: "Completed Demos",
        icon: CheckCircleIcon,
        color: "#10b981",
        path: "/demos",
      },
      {
        value: kpis.cancelledDemos ?? 0,
        label: "Cancelled Demos",
        icon: CancelIcon,
        color: "#ef4444",
        path: "/demos",
      },
    ];
  }, [kpis]);

  const leadTiles = useMemo(() => {
    return [
      {
        value: kpis.totalLeads ?? 0,
        label: "Total Leads",
        icon: AssignmentIcon,
        color: "#6366f1",
        path: "/leads",
      },
      ...LEAD_STAGES.map((stage) => {
        const Icon = stageIcons[stage] || AssignmentIcon;
        const color = stageColors[stage] || "#6366f1";

        return {
          value: getStageCountFromResponse(leadsPerStage, stage),
          label: formatLabel(stage),
          icon: Icon,
          color,
          path: "/leads",
        };
      }),
    ];
  }, [leadsPerStage, kpis.totalLeads]);

  if (loading) {
    return <div style={globalStyles.loading}>Loading dashboard...</div>;
  }

  return (
    <div
      style={{
        ...globalStyles.contentWrapper,
        ...(isMobile ? globalStyles.responsive?.mobileContentWrapper : {}),
      }}
    >
      <h1
        style={{
          ...globalStyles.pageTitle,
          ...(isMobile ? globalStyles.responsive?.mobilePageTitle : {}),
        }}
      >
        Dashboard
      </h1>

      <h2 style={globalStyles.sectionTitle}>Overview</h2>
      <div style={s.dynamicGrid(overviewTiles.length || 1, isMobile)}>
        {overviewTiles.map((tile) => (
          <KPI
            key={tile.label}
            value={tile.value}
            label={tile.label}
            icon={tile.icon}
            color={tile.color}
            onClick={() => navigate(tile.path)}
          />
        ))}
      </div>

      <h2 style={globalStyles.sectionTitle}>Demos</h2>
      <div style={s.dynamicGrid(demoTiles.length || 1, isMobile)}>
        {demoTiles.map((tile) => (
          <KPI
            key={tile.label}
            value={tile.value}
            label={tile.label}
            icon={tile.icon}
            color={tile.color}
            onClick={() => navigate(tile.path)}
          />
        ))}
      </div>

      <h2 style={globalStyles.sectionTitle}>Leads</h2>
      <div style={s.dynamicGrid(leadTiles.length || 1, isMobile)}>
        {leadTiles.map((tile) => (
          <KPI
            key={tile.label}
            value={tile.value}
            label={tile.label}
            icon={tile.icon}
            color={tile.color}
            onClick={() => navigate(tile.path)}
          />
        ))}
      </div>

      <div style={s.tableHeaderRow}>
        <h2 style={s.tableTitle}>Recent Leads</h2>

        <button
          type="button"
          style={s.seeAllBtn}
          onClick={() => navigate("/leads")}
        >
          See All
        </button>
      </div>

      <div style={globalStyles.tableScroll}>
        <table style={globalStyles.table}>
          <thead>
            <tr>
              <th style={globalStyles.th}>Operator</th>
              <th style={globalStyles.th}>Stage</th>
              <th style={globalStyles.th}>Status</th>
              <th style={globalStyles.th}>Assigned To</th>
            </tr>
          </thead>

          <tbody>
            {Array.isArray(recent_leads) && recent_leads.length > 0 ? (
              recent_leads.map((lead) => (
                <tr key={lead._id}>
                  <td style={globalStyles.td}>
                    {getRecentLeadValue(lead, "operator")}
                  </td>
                  <td style={globalStyles.td}>
                    <span
                      style={globalStyles.stageBadge(
                        lead.stage || lead.currentStage,
                      )}
                    >
                      {formatLabel(lead.stage || lead.currentStage)}
                    </span>
                  </td>
                  <td style={globalStyles.td}>
                    <span style={globalStyles.statusBadge(lead.status)}>
                      {formatLabel(lead.status)}
                    </span>
                  </td>
                  <td style={globalStyles.td}>
                    {getRecentLeadValue(lead, "assignedTo")}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan="4"
                  style={{ ...globalStyles.td, ...globalStyles.emptyState }}
                >
                  No Recent Leads Found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
